import { useCallback, useEffect, useState } from 'react';
import { ZOOM_VAR } from '@vitness/tokens';

export const UI_SCALES = [1, 1.1, 1.25, 1.4] as const;
export type UiScale = (typeof UI_SCALES)[number];

export type FormFactor = 'mobile' | 'desktop';

/**
 * Scale is per device class, not one global number.
 *
 * The app was drawn at tight density and reads small at 1:1 on a monitor, but a
 * phone is already held at arm's length with a high-DPI panel — zooming it to
 * 125% just spends a quarter of the screen. So the two form factors get their
 * own default, and their own stored preference.
 */
export const DEFAULT_UI_SCALE: Record<FormFactor, UiScale> = {
  mobile: 1,
  desktop: 1.25,
};

const LS_KEY: Record<FormFactor, string> = {
  mobile: 'vudget-ui-scale-mobile',
  desktop: 'vudget-ui-scale-desktop',
};

/** D1 keys — separate rows, so a phone and a laptop don't overwrite each other. */
export const DB_KEY: Record<FormFactor, string> = {
  mobile: 'ui_scale_mobile',
  desktop: 'ui_scale_desktop',
};

/** Pre-split single-value keys, still read once so an existing choice survives. */
export const LEGACY_LS_KEY = 'vudget-ui-scale';
export const LEGACY_DB_KEY = 'ui_scale';

/**
 * Which device class this is.
 *
 * Deliberately `screen.width`, not `window.innerWidth` and not a media query.
 * The scale is applied as CSS `zoom` on `<html>`, which makes media queries and
 * the layout viewport evaluate against the *scaled* width — so choosing the form
 * factor from either would let the scale decide its own input: pick desktop →
 * zoom to 1.25 → viewport reports 25% narrower → looks like mobile → drop to
 * 1.0 → looks like desktop again. `screen.width` is the physical panel and is
 * unaffected by page zoom, so the decision is stable.
 *
 * 820px covers phones and portrait tablets. Anything wider is a desktop even in
 * a narrow window — this is a property of the device in your hand, not of how
 * far you dragged the window edge.
 */
export function formFactor(): FormFactor {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.screen?.width ?? 1280;
  return w <= 820 ? 'mobile' : 'desktop';
}

export const isUiScale = (n: number): n is UiScale => (UI_SCALES as readonly number[]).includes(n);

/** The stored scale for a device class, falling back to the legacy shared key. */
export function readStoredScale(ff: FormFactor = formFactor()): UiScale {
  if (typeof window === 'undefined') return DEFAULT_UI_SCALE[ff];
  try {
    const own = Number(window.localStorage.getItem(LS_KEY[ff]));
    if (isUiScale(own)) return own;
    // Migration: one shared value used to cover both form factors. Adopt it
    // rather than silently resetting whoever reads it first back to default.
    const legacy = Number(window.localStorage.getItem(LEGACY_LS_KEY));
    if (isUiScale(legacy)) return legacy;
  } catch { /* private mode */ }
  return DEFAULT_UI_SCALE[ff];
}

/**
 * Viewport units do not know about `zoom`.
 *
 * `zoom` scales the used value of every length inside the element, but `100vh`
 * still resolves against the unzoomed viewport first — so at 125% a `100vh`
 * sidebar is laid out 25% taller than the window and its contents pick up a
 * scrollbar with the bottom of the nav pushed off-screen. Percentages are fine
 * (they resolve against an already-scaled parent); viewport units are not.
 *
 * So the scale is published as a custom property too, and anything sized in
 * viewport units divides by it. In TypeScript that is the `vp()` helper; in
 * stylesheets it is written out as `calc(<length> / var(--vd-zoom, 1))`. The
 * fallback of 1 keeps both correct for anyone reading them outside this app.
 *
 * Re-exported from `@vitness/tokens` rather than declared here: the design
 * system's own components (the command palette, the account menu, the mobile
 * drawer) are inside the scaled tree too and need the same correction, and they
 * cannot import from the host app.
 */
export { ZOOM_VAR, vp } from '@vitness/tokens';

export function applyUiScale(scale: number): void {
  document.documentElement.style.zoom = String(scale);
  document.documentElement.style.setProperty(ZOOM_VAR, String(scale));
}

/**
 * Render one route at 1:1 regardless of the stored interface scale.
 *
 * The sign-in screen is a fixed, full-viewport composition — `AuthPage` sizes its
 * two columns off the viewport and expects to fill it exactly. Zooming it to
 * 125% makes it 25% taller than the window, so the whole page scrolls and the
 * brand column's bottom row of trust signals falls below the fold. The dashboard
 * *wants* the zoom (it's dense, at arm's length on a monitor); a single centred
 * card does not.
 *
 * Returns a cleanup that restores the user's scale, so this is safe to drive from
 * an effect — unmounting the route puts the dashboard back where it was.
 */
export function useUnscaled(): void {
  const { scale } = useUiScale();
  useEffect(() => {
    applyUiScale(1);
    return () => applyUiScale(scale);
  }, [scale]);
}

/**
 * Interface scale — the app's own version of browser zoom.
 *
 * Implemented with CSS `zoom` on `<html>`, which is exactly what the browser's
 * own zoom does: every length scales together, and media queries evaluate
 * against the scaled viewport, so breakpoints fire as though the window were
 * proportionally narrower. That last part is why this beats the obvious
 * alternatives — a `transform: scale()` wrapper would enlarge the pixels but
 * leave the layout thinking it still had the full width, and rem-scaling would
 * miss every hard-coded px in the design system.
 *
 * Read synchronously from localStorage and applied before first paint (see
 * main.tsx) so the app doesn't visibly resize on load. D1 is still the source of
 * truth across devices; localStorage is the cache that avoids the flash.
 */
export function useUiScale() {
  const ff = formFactor();
  const [scale, setScaleState] = useState<UiScale>(() => readStoredScale(ff));

  useEffect(() => { applyUiScale(scale); }, [scale]);

  const setScale = useCallback((next: UiScale) => {
    setScaleState(next);
    try { window.localStorage.setItem(LS_KEY[ff], String(next)); } catch { /* private mode */ }
  }, [ff]);

  return { scale, setScale, formFactor: ff };
}
